<template>
  <div>
    <h4 v-if="!usedInCreateNewTenancy">Registrer info om {{ name }}</h4>

    <div class="mt-5">
      <ValidationProvider v-if="!isDepAccountOther" name="Status" rules="required" v-slot="{ errors }">
        <hl-Select
          label="Status for depositumet"
          :value="localStatus"
          :options="statusOptions1"
          placeholder="Velg status"
          :error="errors[0]"
          @input.native="updateLocalStatus"
        />
      </ValidationProvider>

      <hl-Select
        v-else
        label="Status for depositumet"
        :value="localStatus"
        :options="statusOptions2"
        @input.native="updateLocalStatus"
        placeholder="Velg status"
        class="mt-4"
      />
    </div>

    <div class="row mt-5">
      <!-- Supplier -->
      <div v-if="isDepGarSP || isDepGarOther" class="col">
        <hl-Input type="text" label="Leverandør" v-model="localCopy.meta.supplier" :disabled="isDepGarSP" />
      </div>

      <!-- Bank -->
      <div v-if="isDepAccountOther || isBankGar" class="col">
        <hl-Input type="text" label="Bank" v-model="localBank" />
      </div>

      <!-- Policy Number -->
      <div v-if="!isDepAccountOther && !isDepOther" class="col">
        <hl-Input type="text" label="Garantinummer" v-model="localCopy.meta.policyNumber" />
      </div>
    </div>

    <div class="row my-4">
      <div v-if="isDepAccountOther" class="col">
        <hl-Input type="text" label="Bankkonto" v-model="localAccountNumber" />
      </div>

      <div class="col">
        <ValidationProvider
          name="Avtalt depositum"
          :rules="validationRules.tenancy.deposit.newDepositForm.amount"
          v-slot="{ errors }"
        >
          <hl-Input type="number" label="Avtalt depositum (i kr)" v-model="localCopy.amount" :error="errors[0]" />
        </ValidationProvider>
      </div>

      <div v-if="isDepAccountOther || isDepOther" class="col">
        <hl-Input type="number" label="Innbetalt depositum (i kr)" v-model="localBalance" />
      </div>
    </div>

    <div class="row my-4">
      <div v-if="!isDepAccountOther" class="col">
        <hl-Datepicker label="Startdato" v-model="localCopy.meta.startDate"></hl-Datepicker>
      </div>
      <div v-if="!isDepAccountOther" class="col">
        <hl-Datepicker label="Sluttdato" v-model="localCopy.meta.endDate"></hl-Datepicker>
      </div>
    </div>

    <div class="my-4 pt-3">
      <!-- There is a file in edit mode -->
      <div v-if="localCopy.fileMeta">
        <ul class="files-to-be-uploaded list-unstyled text-left">
          <li>
            <i class="budicon-attachment"></i>
            <span class="ml-3">{{ localCopy.fileMeta.fileName }}</span>
          </li>
        </ul>
      </div>

      <!-- There are files -->
      <div v-if="files.length">
        <ul class="files-to-be-uploaded list-unstyled text-left">
          <li v-for="(file, i) in files" :key="i">
            <div class="row align-items-center">
              <div class="col">{{ file.fileName }}</div>
              <div class="col text-secondary text-right">
                <hl-Button variant="white" size="sm" @click="removeFilesToBeUploaded">
                  <i class="budicon-trash"></i>
                </hl-Button>
              </div>
            </div>
          </li>
        </ul>
      </div>

      <!-- NO files. Show upload -->
      <div v-else class="input-group mb-3 mt-2">
        <div class="custom-file">
          <input
            type="file"
            @change="handleFilesUpload()"
            class="custom-file-input"
            id="files"
            ref="files"
            aria-describedby="inputGroupFileAddon01"
          />
          <label class="custom-file-label" for="inputGroupFile01">
            Last opp dokumentasjon om depositumet
          </label>
        </div>
      </div>
    </div>

    <div v-if="isNAVGar">
      <div class="row my-4">
        <div class="col">
          <hl-Input type="text" label="NAV-kontor" v-model="localNAVOffice" />
        </div>

        <div class="col">
          <hl-Input type="text" label="Kontaktperson" v-model="localNAVContactPerson" />
        </div>
      </div>

      <div class="row my-4">
        <div class="col">
          <hl-Input type="text" label="E-post" v-model="localNAVEmail" />
        </div>

        <div class="col">
          <hl-Input type="text" label="Telefon" v-model="localNAVPhone" />
        </div>
      </div>
    </div>

    <div v-if="isDepOther" class="row my-4">
      <div class="col-12 col-md-6">
        <hl-Input type="text" label="Type sikkerhet" v-model="localOtherSecurityType" />
      </div>
    </div>
  </div>
</template>

<script>
import addValidationRules from '@/mixins/addValidationRules.js';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import Datepicker from '@modules/husleie-framework/src/components/forms/inputs/Datepicker.vue';
import Select from '@modules/husleie-framework/src/components/forms/inputs/select/Select.vue';

export default {
  components: {
    'hl-Datepicker': Datepicker,
    'hl-Select': Select,
    'hl-Button': Button,
    'hl-Input': Input,
  },

  props: {
    deposit: { type: Object, required: true },
    usedInCreateNewTenancy: { type: Boolean, default: false },
    name: { type: String, default: '' },
  },

  mixins: [addValidationRules],

  data() {
    return {
      localCopy: {},
      files: [],
    };
  },

  computed: {
    isDepAccountEasybank() {
      return this.deposit.category === 'deposit_easybank';
    },
    isDepAccountOther() {
      return this.deposit.category === 'other_deposit_account';
    },
    isDepGarSP() {
      return this.deposit.category === 'deposit_guarantee';
    },
    isDepGarOther() {
      return this.deposit.category === 'other_deposit_guarantee';
    },
    isBankGar() {
      return this.deposit.category === 'bank_guarantee';
    },
    isNAVGar() {
      return this.deposit.category === 'nav_guarantee';
    },
    isDepOther() {
      return this.deposit.category === 'other';
    },

    // status options
    statusOptions1() {
      return [
        { name: 'Mangler innbetaling', value: 'initial' },
        { name: 'Alt ok', value: 'active' },
        { name: 'Avsluttet', value: 'ended' },
        { name: 'I kravprosess', value: 'conflict' },
      ];
    },

    statusOptions2() {
      return [
        { name: 'Ikke etablert', value: 'initial' },
        { name: 'Ikke innbetalt', value: 'pending' },
        { name: 'Alt ok', value: 'active' },
        { name: 'Avsluttet', value: 'ended' },
        { name: 'I kravprosess', value: 'conflict' },
      ];
    },

    // localCopy
    localStatus() {
      return this.deposit.status;
    },

    localSupplier: {
      get() {
        return this.deposit.supplier;
      },
      set(val) {
        this.localCopy.supplier = val;
      },
    },
    localBank: {
      get() {
        return this.deposit.meta.bank;
      },
      set(val) {
        this.localCopy.meta.bank = val;
      },
    },
    localGuaranteeNumber: {
      get() {
        return this.deposit.policyNumber;
      },
      set(val) {
        this.localCopy.policyNumber = val;
      },
    },
    localAccountNumber: {
      get() {
        return this.deposit.meta.accountNumber;
      },
      set(val) {
        this.localCopy.meta.accountNumber = val;
      },
    },

    localPolicyNumber: {
      get() {
        return this.deposit.policyNumber;
      },
      set(val) {
        this.localCopy.policyNumber = val;
      },
    },

    localAmount: {
      get() {
        return this.deposit.amount;
      },
      set(val) {
        this.localCopy.amount = val;
      },
    },
    localBalance: {
      get() {
        return this.deposit.meta.funds;
      },
      set(val) {
        this.localCopy.meta.funds = val;
      },
    },
    localStartDate: {
      get() {
        return this.deposit.startDate;
      },
      set(val) {
        this.localCopy.startDate = val;
      },
    },
    localEndDate: {
      get() {
        return this.deposit.endDate;
      },
      set(val) {
        this.localCopy.endDate = val;
      },
    },
    localFile: {
      get() {
        return this.deposit.file;
      },
      set(val) {
        this.localCopy.file = val;
      },
    },
    localNAVOffice: {
      get() {
        return this.deposit.NAVOffice;
      },
      set(val) {
        this.localCopy.NAVOffice = val;
      },
    },
    localNAVContactPerson: {
      get() {
        return this.deposit.NAVContactPerson;
      },
      set(val) {
        this.localCopy.NAVContactPerson = val;
      },
    },
    localNAVEmail: {
      get() {
        return this.deposit.NAVEmail;
      },
      set(val) {
        this.localCopy.NAVEmail = val;
      },
    },
    localNAVPhone: {
      get() {
        return this.deposit.NAVPhone;
      },
      set(val) {
        this.localCopy.NAVPhone = val;
      },
    },
    localOtherSecurityType: {
      get() {
        return this.deposit.otherSecurityType;
      },
      set(val) {
        this.localCopy.otherSecurityType = val;
      },
    },
  },

  methods: {
    updateLocalStatus(e) {
      this.localCopy.status = e.target.value;
    },

    handleFilesUpload() {
      const uploadedFiles = this.$refs.files.files;

      if (uploadedFiles.length === 0) {
        return;
      }

      if (uploadedFiles.length > 1) {
        this.$toast('Kun én fil kan lastes opp for depositum. Kun første fil akseptert.', 'warning');
      }

      this.files = [{
        order: 1,
        file: uploadedFiles[0],
        fileName: uploadedFiles[0].name,
        uploaded: false,
      }];

      this.$emit('gotFiles', this.files);
    },

    removeFilesToBeUploaded() {
      this.files = [];
    },
  },

  mounted() {
    this.localCopy = JSON.parse(JSON.stringify(this.deposit));
  },

  watch: {
    localCopy: {
      async handler(newVal) {
        this.$emit('updatedLocalCopy', newVal);
      },
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
.files-to-be-uploaded {
  margin-top: 1.5rem;

  li {
    font-weight: bold;
    padding: 1rem;
    background: $gray-100;
    border: 1px solid $gray-200;
    border-radius: $border-radius-medium;
  }
}
</style>
